<template>
  <base-dialog
    v-model="openModal"
    :fullscreen="paymentUrl"
    :max-width="maxWidth"
    :max-height="1500"
    class="payment-handler-dialog"
  >
    <template #content>
      <div class="payment-content">
        <h1 v-if="!paymentUrl && !showContactUs" class="subscription-title">
          {{
            type === 'ai'
              ? $t('generics.buyAiCredits')
              : $t('subscription.chooseSubscription')
          }}
        </h1>

        <div
          class="currency-select-container"
          :class="{ ltr: locale === 'en' }"
          v-if="!paymentUrl && !showContactUs"
        >
          <v-select
            v-model="selectedCurrency"
            :items="currencyOptions"
            item-title="text"
            item-value="value"
            density="compact"
            variant="outlined"
            class="currency-select"
          />
        </div>

        <v-row
          v-if="!paymentUrl && !showContactUs"
          class="subscription-options"
        >
          <v-col
            v-for="(item, index) in visibleSubscriptions"
            :key="item._id + index"
            class="subscription-card"
            :class="{
              'sale-card': !!getLocalizedSaleTitle(item),
              'selected-card': type === 'game' && selectedPlanId === item._id,
            }"
            @click="onPlanClick(item._id)"
            v-ripple
          >
            <div v-if="getLocalizedSaleTitle(item)" class="sale-badge">
              {{ getLocalizedSaleTitle(item) }}
            </div>
            <span class="subscription-title-text">
              {{ getLocalizedTitle(item) }}
            </span>
            <span class="subscription-description">
              {{ getLocalizedDescription(item) }}
            </span>
            <span class="subscription-price">
              <template v-if="isIsrael">
                <template v-if="type === 'game' && appliedCoupon">
                  <span class="price-original">
                    <span class="price-value">{{ item.price }}</span>
                    <span class="currency">{{ $t('generics.ins') }}</span>
                  </span>
                  <span class="price-value price-final">
                    {{ getPlanFinalPrice(item) }}
                  </span>
                  <span class="currency">{{ $t('generics.ins') }}</span>
                </template>
                <template v-else>
                  <span class="price-value">{{ item.price }}</span>
                  <span class="currency">{{ $t('generics.ins') }}</span>
                </template>
              </template>
              <template v-else>
                <template v-if="type === 'game' && appliedCoupon">
                  <span class="price-original">
                    <span class="currency">$</span>
                    <span class="price-value">{{ item.dollars }}</span>
                  </span>
                  <span class="currency">$</span>
                  <span class="price-value price-final">
                    {{ getPlanFinalPrice(item) }}
                  </span>
                </template>
                <template v-else>
                  <span class="currency">$</span>
                  <span class="price-value">{{ item.dollars }}</span>
                </template>
              </template>
            </span>
          </v-col>
        </v-row>

        <div
          v-if="!paymentUrl && !showContactUs && type === 'game'"
          class="coupon-section"
        >
          <div class="coupon-row">
            <v-text-field
              v-model="couponCode"
              :label="$t('coupon.enterCode')"
              density="compact"
              variant="outlined"
              hide-details="auto"
              :error-messages="couponError"
              class="coupon-input"
              @keydown.enter.prevent="applyCoupon"
            />
            <v-btn
              color="primary"
              variant="tonal"
              :loading="validatingCoupon"
              :disabled="!couponCode.trim()"
              @click="applyCoupon"
            >
              {{ $t('coupon.apply') }}
            </v-btn>
          </div>
          <p v-if="appliedCoupon" class="coupon-success">
            {{ couponAppliedMessage }}
          </p>
        </div>

        <div
          v-if="!paymentUrl && !showContactUs && type === 'game'"
          class="continue-section"
        >
          <v-btn
            color="primary"
            size="large"
            rounded="xl"
            class="continue-btn"
            :disabled="!selectedPlanId"
            :loading="startingPayment"
            @click="continueToPayment"
          >
            {{ $t('generics.continue') }}
          </v-btn>
        </div>

        <div
          v-if="!paymentUrl && showContactUs"
          class="d-flex flex-column align-center justify-center py-10"
        >
          <v-icon size="100" color="primary" class="mb-5"
            >mdi-office-building</v-icon
          >
          <h2 class="contact-title mb-4 text-center">
            {{ $t('games.remoteCreditInformNoCredits') }}
          </h2>
          <p class="contact-subtitle mb-8 text-center text-medium-emphasis">
            {{ $t('generics.institutionContactMessage') }}
          </p>
          <v-btn
            :href="waLink"
            target="_blank"
            color="success"
            size="large"
            rounded="xl"
            prepend-icon="mdi-whatsapp"
            class="px-8"
          >
            {{ $t('contact.whatsapp') }}
          </v-btn>
        </div>

        <iframe
          v-if="paymentUrl"
          class="payment-iframe"
          :src="paymentUrl"
          title="payment"
          allow="payment"
          referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>
      </div>
    </template>
  </base-dialog>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSubscriptionStore } from '@/store/subscription'
import { useAuthStore } from '@/store/auth'
import { storeToRefs } from 'pinia'
import { startPayment } from '@/api/pay.api'
import { validateCoupon } from '@/api/coupon.api'
import { trackFBEvent } from '@/composables/useLoadScripts'
import { detectIsrael } from '@/utils/detectIsrael'
import { extractClapError } from '@/helpers/errorHelper'

const openModal = defineModel('modelValue', {
  type: Boolean,
})

const props = defineProps({
  type: {
    type: String,
    default: 'game', // 'game' | 'ai'
  },
})

const { locale, t } = useI18n()
const router = useRouter()

function resolveErrorMessage(e) {
  const data = e?.response?.data
  if (!data) return extractClapError(e)
  const lang = locale.value
  if (data.clapErr?.[lang]) return data.clapErr[lang]
  if (data[lang]) return data[lang]
  if (data.he) return data.he
  if (data.err) return data.err
  return extractClapError(e)
}
const selectedPlanId = ref(null)
const paymentUrl = ref(null)
const paymentPublicId = ref(null)
const selectedCurrency = ref(null)
const couponCode = ref('')
const couponError = ref('')
const appliedCoupon = ref(null)
const validatingCoupon = ref(false)
const startingPayment = ref(false)

const authStore = useAuthStore()
const { isInstitution, isAdmin } = storeToRefs(authStore)

const subscriptionStore = useSubscriptionStore()
const { subscriptions: allSubscriptions } = storeToRefs(subscriptionStore)
const { getSubscriptions } = subscriptionStore

const subscriptions = computed(() => {
  return allSubscriptions.value.filter(s => (s.type || 'game') === props.type)
})

const showContactUs = computed(() => isInstitution.value && !isAdmin.value)

const waLink = computed(() => `https://wa.me/972523309773`)

const currencyOptions = computed(() => [
  { text: `${t('generics.dollar')} ${t('generics.USD')}`, value: 'usd' },
  { text: `₪ ${t('generics.ILS')}`, value: 'ils' },
])

const isIsrael = computed(() => selectedCurrency.value === 'ils')
const visibleSubscriptions = computed(() => {
  return subscriptions.value.filter(item => {
    if (selectedCurrency.value === 'usd') {
      return item.dollars && Number(item.dollars) > 0
    }
    if (selectedCurrency.value === 'ils') {
      return item.price && Number(item.price) > 0
    }
    return true
  })
})
const maxWidth = computed(() => (paymentUrl.value ? 900 : 780))

function calculateDiscount(originalPrice, discountType, discountValue) {
  const price = Number(originalPrice) || 0
  const value = Number(discountValue) || 0
  let discountAmount = 0

  if (discountType === 'percent') {
    discountAmount = (price * value) / 100
  } else if (discountType === 'amount') {
    discountAmount = value
  }

  discountAmount = Math.min(Math.max(0, discountAmount), price)
  const finalPrice = Math.max(
    0,
    Math.round((price - discountAmount) * 100) / 100
  )

  return finalPrice
}

function getPlanOriginalPrice(item) {
  return isIsrael.value ? Number(item.price) || 0 : Number(item.dollars) || 0
}

function getPlanFinalPrice(item) {
  if (!appliedCoupon.value) return getPlanOriginalPrice(item)
  return calculateDiscount(
    getPlanOriginalPrice(item),
    appliedCoupon.value.discountType,
    appliedCoupon.value.discountValue
  )
}

const couponAppliedMessage = computed(() => {
  if (!appliedCoupon.value) return ''
  const { discountType, discountValue } = appliedCoupon.value
  if (discountType === 'percent') {
    return t('coupon.appliedPercent', { value: discountValue })
  }
  return t('coupon.applied', {
    amount: discountValue,
    currency: isIsrael.value ? t('generics.ins') : '$',
  })
})

function getLocalizedTitle(item) {
  return locale.value === 'he'
    ? item.title
    : locale.value === 'en'
      ? item.titleEn || item.title
      : locale.value === 'ar'
        ? item.titleAr || item.title
        : item.title
}

function getLocalizedDescription(item) {
  return locale.value === 'he'
    ? item.description
    : locale.value === 'en'
      ? item.descriptionEn || item.description
      : locale.value === 'ar'
        ? item.descriptionAr || item.description
        : item.description
}

function getLocalizedSaleTitle(item) {
  return locale.value === 'he'
    ? item.saleTitle
    : locale.value === 'en'
      ? item.saleTitleEn || item.saleTitle
      : locale.value === 'ar'
        ? item.saleTitleAr || item.saleTitle
        : item.saleTitle
}

function clearCouponState() {
  couponCode.value = ''
  couponError.value = ''
  appliedCoupon.value = null
}

function onPlanClick(subscriptionType) {
  if (props.type === 'ai') {
    selectSubscription(subscriptionType)
    return
  }
  selectedPlanId.value = subscriptionType
}

async function applyCoupon() {
  if (!couponCode.value.trim()) return
  validatingCoupon.value = true
  couponError.value = ''
  try {
    const result = await validateCoupon({
      code: couponCode.value.trim(),
      ...(selectedPlanId.value
        ? { subscriptionId: selectedPlanId.value, israel: isIsrael.value }
        : {}),
    })
    appliedCoupon.value = result
  } catch (e) {
    appliedCoupon.value = null
    couponError.value = resolveErrorMessage(e)
  } finally {
    validatingCoupon.value = false
  }
}

async function continueToPayment() {
  if (!selectedPlanId.value) return
  await selectSubscription(
    selectedPlanId.value,
    appliedCoupon.value?.code || couponCode.value.trim() || null
  )
}

async function selectSubscription(subscriptionType, code = null) {
  startingPayment.value = true
  try {
    trackFBEvent({ eventType: 'InitiateCheckout' })
    localStorage.setItem('paymentStarted', 'true')
    const data = await startPayment(
      locale.value,
      subscriptionType,
      isIsrael.value,
      code
    )
    if (data.free) {
      openModal.value = false
      await router.push({
        name: 'PaymentSuccess',
        params: { lang: locale.value },
      })
      return
    }
    paymentPublicId.value = data.token
    paymentUrl.value = data.url
  } catch (e) {
    couponError.value = resolveErrorMessage(e)
  } finally {
    startingPayment.value = false
  }
}

watch(selectedCurrency, () => {
  clearCouponState()
  if (props.type === 'game') {
    selectedPlanId.value = null
  }
})

watch(couponCode, () => {
  if (appliedCoupon.value) {
    appliedCoupon.value = null
  }
  couponError.value = ''
})

onMounted(async () => {
  await getSubscriptions()
  selectedCurrency.value = detectIsrael() ? 'ils' : 'usd'
})
</script>

<style scoped>
.subscription-title {
  font-size: 24px;
  font-weight: 500;
  line-height: 35.27px;
  text-align: center;
  margin: 0 auto;
  z-index: 2;
}

.subscription-options {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  margin-bottom: 24px;
  margin-left: 7px;
  margin-right: 7px;
  gap: 19px;
}

.subscription-card {
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.11);
  min-height: 225px;
  width: 216px;
  max-width: 216px;
  cursor: pointer;
  position: relative;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px;
}

.selected-card {
  outline: 3px solid #4a72ff;
  outline-offset: 2px;
}

.sale-badge {
  position: absolute;
  min-width: 81px;
  height: 30px;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 13px;
  display: grid;
  place-items: center;
  font-size: 16px;
  font-weight: 500;
  background: linear-gradient(99.39deg, #d973fe 0%, #4a72ff 98.23%);
  box-shadow: 0px 4px 0px 0px rgba(0, 25, 128, 1);
  color: white;
}

.subscription-title-text {
  margin-top: 30px;
  font-size: 24px;
  font-weight: 500;
  line-height: 35.27px;
  text-align: center;
}

.subscription-description {
  margin-top: 30px;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.52px;
  text-align: center;
  width: 150px;
  margin-bottom: 80px;
  white-space: pre-line;
}

.subscription-price {
  display: flex;
  align-items: center;
  margin-bottom: 3px;
  gap: 8px;
  position: absolute;
  bottom: 0;
  flex-wrap: wrap;
  justify-content: center;
}

.price-value {
  font-size: 32px;
  font-weight: 500;
  line-height: 47.03px;
}

.price-final {
  color: #2e7d32;
}

.price-original {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0.55;
  text-decoration: line-through;
}

.price-original .price-value {
  font-size: 20px;
  line-height: 1.2;
}

.price-original .currency {
  font-size: 12px;
  margin-top: 0;
}

.currency {
  font-size: 16px;
  font-weight: 500;
  line-height: 23.52px;
  margin-top: 8px;
}

.payment-iframe {
  border: none;
  width: 100%;
  height: 88vh;
}

.sale-card {
  background:
    linear-gradient(#ffffff 0 0) padding-box,
    var(--linearHotPinkDodgerBlue) border-box;
  border: 3px solid transparent;
}

.currency-select-container {
  position: absolute;
  right: 15px;
  top: 15px;
  z-index: 2;
  width: 120px;
}
.ltr.currency-select-container {
  right: unset;
  left: 15px;
}

.currency-select {
  min-width: 100px;
}

.payment-content {
  max-height: 76vh;
  padding-bottom: 24px;
}

.contact-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.contact-subtitle {
  font-size: 20px;
  line-height: 1.4;
}

.coupon-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 0 auto 24px;
  max-width: 480px;
  width: 100%;
  padding: 0 16px;
}

.coupon-row {
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: flex-start;
}

.coupon-input {
  flex: 1;
}

.coupon-success {
  margin: 0;
  color: #2e7d32;
  font-size: 14px;
  text-align: center;
}

.continue-section {
  display: flex;
  justify-content: center;
  margin: 8px auto 24px;
}

.continue-btn {
  min-width: 180px;
}
</style>
